import React, { useState, useEffect, useMemo } from 'react';
import { GameUserStatsResponse, CurrencyOpenStat, UserTopEntry, DailyUserAnalyticsPoint, CohortRetentionPoint, CurrencyInfo } from '../types';
import { apiService } from '../api';
import LoadingSpinner from './LoadingSpinner';
import { BarChart, ChartDataPoint } from './charts';

type TopType = 'volume' | 'ggr' | 'win' | 'spins';

interface TopConfig {
  startDate: string;
  endDate: string;
  timezoneOffset: number;
  loading: boolean;
  data: UserTopEntry[] | null;
  error: string;
  expanded: boolean; // Whether the top section is expanded
}

interface GameUserStatsPageProps {
  gameId: number;
  gameName: string;
  onBack: () => void;
}

// Get user's timezone offset (in hours, from -12 to +12)
const getUserTimezoneOffset = (): number => {
  const offsetMinutes = new Date().getTimezoneOffset();
  // getTimezoneOffset returns offset in minutes, negative for positive timezones
  // Convert to hours and invert sign
  const offsetHours = -offsetMinutes / 60;
  // Clamp to valid range [-12, 12]
  return Math.max(-12, Math.min(12, Math.round(offsetHours)));
};

const GameUserStatsPage: React.FC<GameUserStatsPageProps> = ({ gameId, gameName, onBack }) => {
  const [stats, setStats] = useState<GameUserStatsResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [timezoneOffset, setTimezoneOffset] = useState<number>(getUserTimezoneOffset());
  const [dailyAnalytics, setDailyAnalytics] = useState<DailyUserAnalyticsPoint[]>([]);
  const [dailyLoading, setDailyLoading] = useState<boolean>(false);
  const [dailyError, setDailyError] = useState<string>('');
  const [retentionData, setRetentionData] = useState<CohortRetentionPoint[]>([]);
  const [retentionLoading, setRetentionLoading] = useState<boolean>(false);
  const [retentionError, setRetentionError] = useState<string>('');
  const [retStartDate, setRetStartDate] = useState<string>('');
  const [retEndDate, setRetEndDate] = useState<string>('');
  const [retTimezone, setRetTimezone] = useState<number>(getUserTimezoneOffset());
  const [retCurrency, setRetCurrency] = useState<string>('');
  const [currencies, setCurrencies] = useState<CurrencyInfo[]>([]);
  
  // Top users state
  const [topConfigs, setTopConfigs] = useState<Record<TopType, TopConfig>>({
    volume: {
      startDate: '',
      endDate: '',
      timezoneOffset: getUserTimezoneOffset(),
      loading: false,
      data: null,
      error: '',
      expanded: true,
    },
    ggr: {
      startDate: '',
      endDate: '',
      timezoneOffset: getUserTimezoneOffset(),
      loading: false,
      data: null,
      error: '',
      expanded: true,
    },
    win: {
      startDate: '',
      endDate: '',
      timezoneOffset: getUserTimezoneOffset(),
      loading: false,
      data: null,
      error: '',
      expanded: true,
    },
    spins: {
      startDate: '',
      endDate: '',
      timezoneOffset: getUserTimezoneOffset(),
      loading: false,
      data: null,
      error: '',
      expanded: true,
    },
  });

  useEffect(() => {
    apiService.getCurrencies().then(res => {
      if (res.success) setCurrencies(res.currencies || []);
    }).catch(() => {});
  }, []);

  // Initialize top configs with current date
  useEffect(() => {
    const today = new Date();
    const dateStr = today.toISOString().split('T')[0];
    setTopConfigs(prev => ({
      volume: { ...prev.volume, startDate: dateStr, endDate: dateStr },
      ggr: { ...prev.ggr, startDate: dateStr, endDate: dateStr },
      win: { ...prev.win, startDate: dateStr, endDate: dateStr },
      spins: { ...prev.spins, startDate: dateStr, endDate: dateStr },
    }));
    setRetStartDate(dateStr);
    setRetEndDate(dateStr);
  }, []);

  const loadStats = async () => {
    if (!startDate || !endDate) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      const request: any = { gameId };
      
      // Create date in UTC from the date string (YYYY-MM-DD)
      const startUTC = new Date(startDate + 'T00:00:00Z');
      // Apply timezone offset
      request.startDate = startUTC.getTime() - (timezoneOffset * 60 * 60 * 1000);
      
      // Create end date in UTC
      const endUTC = new Date(endDate + 'T23:59:59.999Z');
      // Apply timezone offset
      request.endDate = endUTC.getTime() - (timezoneOffset * 60 * 60 * 1000);
      
      const response = await apiService.getGameUserStats(request);
      if (response.success) {
        setStats(response);
      } else {
        setError(response.error || 'Failed to load user statistics');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load user statistics');
      console.error('Game user stats error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadDailyAnalytics = async (sDate: string, eDate: string, tzOffset: number) => {
    if (!sDate || !eDate) return;
    try {
      setDailyLoading(true);
      setDailyError('');
      const startUTC = new Date(sDate + 'T00:00:00Z');
      const startTs = startUTC.getTime() - (tzOffset * 60 * 60 * 1000);
      const endUTC = new Date(eDate + 'T23:59:59.999Z');
      const endTs = endUTC.getTime() - (tzOffset * 60 * 60 * 1000);

      const response = await apiService.getDailyUserAnalytics({
        gameId,
        startDate: startTs,
        endDate: endTs,
        timezoneOffset: tzOffset,
      });
      if (response.success) {
        setDailyAnalytics(response.data || []);
      } else {
        setDailyError(response.error || 'Failed to load daily analytics');
      }
    } catch (err: any) {
      setDailyError(err.response?.data?.error || 'Failed to load daily analytics');
    } finally {
      setDailyLoading(false);
    }
  };

  const loadRetention = async (sDate: string, eDate: string, tzOffset: number, cur: string) => {
    if (!sDate || !eDate) return;
    try {
      setRetentionLoading(true);
      setRetentionError('');
      const startUTC = new Date(sDate + 'T00:00:00Z');
      const startTs = startUTC.getTime() - (tzOffset * 60 * 60 * 1000);
      const endUTC = new Date(eDate + 'T23:59:59.999Z');
      const endTs = endUTC.getTime() - (tzOffset * 60 * 60 * 1000);

      const response = await apiService.getCohortRetention({
        gameId,
        startDate: startTs,
        endDate: endTs,
        timezoneOffset: tzOffset,
        currency: cur || undefined,
      });
      if (response.success) {
        setRetentionData(response.data || []);
      } else {
        setRetentionError(response.error || 'Failed to load retention data');
      }
    } catch (err: any) {
      setRetentionError(err.response?.data?.error || 'Failed to load retention data');
    } finally {
      setRetentionLoading(false);
    }
  };

  // Set default to current date and load initial stats
  useEffect(() => {
    const today = new Date();
    const dateStr = today.toISOString().split('T')[0];
    setStartDate(dateStr);
    setEndDate(dateStr);
    
    // Load initial stats with current date
    const loadInitialStats = async () => {
      try {
        setLoading(true);
        setError('');
        const request: any = { gameId };
        
        // Create date in UTC from the date string (YYYY-MM-DD)
        const startUTC = new Date(dateStr + 'T00:00:00Z');
        // Apply timezone offset
        request.startDate = startUTC.getTime() - (timezoneOffset * 60 * 60 * 1000);
        
        // Create end date in UTC
        const endUTC = new Date(dateStr + 'T23:59:59.999Z');
        // Apply timezone offset
        request.endDate = endUTC.getTime() - (timezoneOffset * 60 * 60 * 1000);
        
        const response = await apiService.getGameUserStats(request);
        if (response.success) {
          setStats(response);
        } else {
          setError(response.error || 'Failed to load user statistics');
        }
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to load user statistics');
        console.error('Game user stats error:', err);
      } finally {
        setLoading(false);
      }
    };
    loadInitialStats();
    loadDailyAnalytics(dateStr, dateStr, timezoneOffset);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId]);

  const handlePeriodSubmit = async () => {
    if (!startDate || !endDate) {
      setError('Please select both start and end dates');
      return;
    }

    const start = new Date(startDate);
    const end = new Date(endDate);
    const now = new Date();
    now.setHours(23, 59, 59, 999);

    if (end > now) {
      setError('End date cannot be in the future');
      return;
    }

    if (start > end) {
      setError('Start date must be before end date');
      return;
    }

    await Promise.all([loadStats(), loadDailyAnalytics(startDate, endDate, timezoneOffset)]);
  };

  const getMaxDate = (): string => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const isDatesValid = (): boolean => {
    if (!startDate || !endDate) return false;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const now = new Date();
    now.setHours(23, 59, 59, 999);
    return start <= end && end <= now;
  };

  // Generate timezone options from UTC-12 to UTC+12
  const timezoneOptions = Array.from({ length: 25 }, (_, i) => {
    const offset = i - 12; // From -12 to +12
    const sign = offset >= 0 ? '+' : '';
    return { value: offset, label: `UTC${sign}${offset}` };
  });

  const loadTop = async (topType: TopType) => {
    const config = topConfigs[topType];
    if (!config.startDate || !config.endDate) {
      setTopConfigs(prev => ({
        ...prev,
        [topType]: { ...prev[topType], error: 'Please select both start and end dates' },
      }));
      return;
    }

    try {
      setTopConfigs(prev => ({
        ...prev,
        [topType]: { ...prev[topType], loading: true, error: '' },
      }));

      // Create date in UTC from the date string
      const startUTC = new Date(config.startDate + 'T00:00:00Z');
      const startTimestamp = startUTC.getTime() - (config.timezoneOffset * 60 * 60 * 1000);
      
      const endUTC = new Date(config.endDate + 'T23:59:59.999Z');
      const endTimestamp = endUTC.getTime() - (config.timezoneOffset * 60 * 60 * 1000);

      const response = await apiService.getGameUserTop({
        gameId,
        topType,
        startDate: startTimestamp,
        endDate: endTimestamp,
        limit: 10,
      });

      if (response.success) {
        setTopConfigs(prev => ({
          ...prev,
          [topType]: { ...prev[topType], data: response.top, loading: false },
        }));
      } else {
        setTopConfigs(prev => ({
          ...prev,
          [topType]: { ...prev[topType], error: response.error || 'Failed to load top users', loading: false },
        }));
      }
    } catch (err: any) {
      setTopConfigs(prev => ({
        ...prev,
        [topType]: { 
          ...prev[topType], 
          error: err.response?.data?.error || 'Failed to load top users', 
          loading: false 
        },
      }));
    }
  };

  const updateTopConfig = (topType: TopType, field: keyof TopConfig, value: any) => {
    setTopConfigs(prev => ({
      ...prev,
      [topType]: { ...prev[topType], [field]: value },
    }));
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatNumber = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const isTopDatesValid = (topType: TopType): boolean => {
    const config = topConfigs[topType];
    if (!config.startDate || !config.endDate) return false;
    const start = new Date(config.startDate);
    const end = new Date(config.endDate);
    const now = new Date();
    now.setHours(23, 59, 59, 999);
    return start <= end && end <= now;
  };

  const formatTopValue = (topType: TopType, value: number): string => {
    switch (topType) {
      case 'volume':
      case 'ggr':
      case 'win':
        // Value is already in dollars (converted from cents on backend)
        return formatCurrency(value);
      case 'spins':
        // Value is spin count 
        return formatNumber(value);
      default:
        return value.toString();
    }
  };

  const dailyChartData: ChartDataPoint<number>[] = useMemo(() => {
    return dailyAnalytics.map(p => ({
      x: p.timestamp,
      series: {
        uniqueUsers: p.uniqueUsers,
        newUsers: p.newUsers,
      },
    }));
  }, [dailyAnalytics]);

  const formatDayLabel = (ts: number): string => {
    const d = new Date(ts);
    const offsetMs = timezoneOffset * 60 * 60 * 1000;
    const local = new Date(d.getTime() + offsetMs);
    return `${String(local.getUTCDate()).padStart(2, '0')}.${String(local.getUTCMonth() + 1).padStart(2, '0')}`;
  };

  const formatDayLabelFull = (ts: number): string => {
    const d = new Date(ts);
    const offsetMs = timezoneOffset * 60 * 60 * 1000;
    const local = new Date(d.getTime() + offsetMs);
    return `${String(local.getUTCDate()).padStart(2, '0')}.${String(local.getUTCMonth() + 1).padStart(2, '0')}.${local.getUTCFullYear()}`;
  };

  const isRetDatesValid = (): boolean => {
    if (!retStartDate || !retEndDate) return false;
    const s = new Date(retStartDate);
    const e = new Date(retEndDate);
    const now = new Date();
    now.setHours(23, 59, 59, 999);
    return s <= e && e <= now;
  };

  const handleRetentionSubmit = async () => {
    if (!isRetDatesValid()) return;
    await loadRetention(retStartDate, retEndDate, retTimezone, retCurrency);
  };

  const retentionChartData: ChartDataPoint<number>[] = useMemo(() => {
    return retentionData.map(p => ({
      x: p.timestamp,
      series: {
        d1Rate: p.d1Rate,
        d7Rate: p.d7Rate,
        d30Rate: p.d30Rate,
      },
    }));
  }, [retentionData]);

  const formatRetDayLabel = (ts: number): string => {
    const d = new Date(ts);
    const offsetMs = retTimezone * 60 * 60 * 1000;
    const local = new Date(d.getTime() + offsetMs);
    return `${String(local.getUTCDate()).padStart(2, '0')}.${String(local.getUTCMonth() + 1).padStart(2, '0')}`;
  };

  const formatRetDayLabelFull = (ts: number): string => {
    const d = new Date(ts);
    const offsetMs = retTimezone * 60 * 60 * 1000;
    const local = new Date(d.getTime() + offsetMs);
    return `${String(local.getUTCDate()).padStart(2, '0')}.${String(local.getUTCMonth() + 1).padStart(2, '0')}.${local.getUTCFullYear()}`;
  };

  if (loading && !stats) {
    return <LoadingSpinner />;
  }

  return (
    <div className="game-user-stats-page" style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ margin: 0, marginBottom: '5px' }}>User Statistics - {gameName}</h1>
          <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>Game ID: {gameId}</p>
        </div>
        <button onClick={onBack} className="btn btn-logout" style={{ padding: '8px 16px' }}>
          ← Back
        </button>
      </div>

      {/* Date range selector */}
      <div style={{ 
        marginBottom: '20px', 
        padding: '15px', 
        backgroundColor: '#f9f9f9', 
        borderRadius: '8px',
        border: '1px solid #ddd'
      }}>
        <div style={{ marginBottom: '10px', fontWeight: '500', color: '#333' }}>
          Select Date Range
        </div>
        <div style={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <label style={{ fontSize: '12px', color: '#666' }}>Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              max={getMaxDate()}
              style={{
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                minWidth: '150px'
              }}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <label style={{ fontSize: '12px', color: '#666' }}>End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              max={getMaxDate()}
              style={{
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                minWidth: '150px'
              }}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <label style={{ fontSize: '12px', color: '#666' }}>Timezone</label>
            <select
              value={timezoneOffset}
              onChange={(e) => setTimezoneOffset(Number(e.target.value))}
              style={{
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                minWidth: '150px',
                backgroundColor: 'white',
                cursor: 'pointer'
              }}
            >
              {timezoneOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={handlePeriodSubmit}
            disabled={!isDatesValid() || loading}
            style={{
              padding: '10px 24px',
              backgroundColor: isDatesValid() && !loading ? '#4CAF50' : '#ccc',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isDatesValid() && !loading ? 'pointer' : 'not-allowed',
              fontSize: '14px',
              fontWeight: '500',
              marginTop: '20px',
              transition: 'all 0.2s ease'
            }}
          >
            Apply Period
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message" style={{ 
          padding: '12px', 
          backgroundColor: '#fee', 
          color: '#c33', 
          borderRadius: '4px',
          marginBottom: '20px'
        }}>
          {error}
        </div>
      )}

      {stats && (
        <>
          {/* Overall Statistics */}
          <div style={{ marginBottom: '30px' }}>
            <h2 style={{ marginBottom: '15px', color: '#333' }}>User Statistics</h2>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
              gap: '15px',
              marginBottom: '20px'
            }}>
              <div style={{ 
                padding: '20px', 
                backgroundColor: 'white', 
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                border: '1px solid #e0e0e0'
              }}>
                <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>Total Users (Played)</div>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#333' }}>
                  {stats.totalUsers.toLocaleString()}
                </div>
              </div>
              <div style={{ 
                padding: '20px', 
                backgroundColor: 'white', 
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                border: '1px solid #e0e0e0'
              }}>
                <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>Total Opens</div>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#333' }}>
                  {stats.totalOpens.toLocaleString()}
                </div>
              </div>
            </div>
          </div>

          {/* Currency Opens Statistics */}
          <div>
            <h2 style={{ marginBottom: '15px', color: '#333' }}>Opens by Currency</h2>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ 
                width: '100%', 
                borderCollapse: 'collapse',
                backgroundColor: 'white',
                borderRadius: '8px',
                overflow: 'hidden',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>
                <thead>
                  <tr style={{ backgroundColor: '#f5f5f5' }}>
                    <th style={{ 
                      padding: '12px', 
                      textAlign: 'left', 
                      borderBottom: '2px solid #ddd'
                    }}>
                      Currency
                    </th>
                    <th style={{ 
                      padding: '12px', 
                      textAlign: 'right', 
                      borderBottom: '2px solid #ddd'
                    }}>
                      Opens
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {stats.currencyOpens.length === 0 ? (
                    <tr>
                      <td colSpan={2} style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                        No currency statistics available
                      </td>
                    </tr>
                  ) : (
                    stats.currencyOpens.map((stat: CurrencyOpenStat) => (
                      <tr key={stat.currency} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '12px', fontWeight: '500' }}>{stat.currency}</td>
                        <td style={{ padding: '12px', textAlign: 'right' }}>{stat.opens.toLocaleString()}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Daily User Analytics Chart */}
      <div style={{ marginTop: '30px', marginBottom: '30px' }}>
        <h2 style={{ marginBottom: '15px', color: '#333' }}>Daily User Analytics</h2>
        {dailyLoading && <LoadingSpinner />}
        {dailyError && (
          <div style={{ padding: '12px', backgroundColor: '#fee', color: '#c33', borderRadius: '4px', marginBottom: '10px' }}>
            {dailyError}
          </div>
        )}
        {!dailyLoading && !dailyError && dailyChartData.length > 0 && (
          <div style={{ 
            backgroundColor: 'white', 
            borderRadius: '8px', 
            padding: '20px', 
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            border: '1px solid #e0e0e0'
          }}>
            <BarChart<number>
              data={dailyChartData}
              formatXLabel={formatDayLabel}
              formatXLabelFull={formatDayLabelFull}
              formatYValue={(v) => v.toLocaleString()}
              seriesLabels={{
                uniqueUsers: 'Unique Users',
                newUsers: 'New Users',
              }}
              colors={['#2196F3', '#4CAF50']}
              height={400}
              minBarGroupWidth={60}
              showLegend={true}
            />
          </div>
        )}
        {!dailyLoading && !dailyError && dailyChartData.length === 0 && stats && (
          <div style={{ padding: '20px', textAlign: 'center', color: '#666', backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
            No daily analytics data available for selected period
          </div>
        )}
      </div>

      {/* Cohort Retention */}
      <div style={{ marginTop: '30px', marginBottom: '30px' }}>
        <h2 style={{ marginBottom: '15px', color: '#333' }}>Cohort Retention</h2>
        <div style={{
          marginBottom: '15px',
          padding: '15px',
          backgroundColor: '#f9f9f9',
          borderRadius: '8px',
          border: '1px solid #ddd'
        }}>
          <div style={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              <label style={{ fontSize: '12px', color: '#666' }}>Start Date</label>
              <input
                type="date"
                value={retStartDate}
                onChange={(e) => setRetStartDate(e.target.value)}
                max={getMaxDate()}
                style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px', minWidth: '150px' }}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              <label style={{ fontSize: '12px', color: '#666' }}>End Date</label>
              <input
                type="date"
                value={retEndDate}
                onChange={(e) => setRetEndDate(e.target.value)}
                max={getMaxDate()}
                style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px', minWidth: '150px' }}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              <label style={{ fontSize: '12px', color: '#666' }}>Timezone</label>
              <select
                value={retTimezone}
                onChange={(e) => setRetTimezone(Number(e.target.value))}
                style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px', minWidth: '150px', backgroundColor: 'white', cursor: 'pointer' }}
              >
                {timezoneOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              <label style={{ fontSize: '12px', color: '#666' }}>Currency (optional)</label>
              <select
                value={retCurrency}
                onChange={(e) => setRetCurrency(e.target.value)}
                style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px', minWidth: '120px', backgroundColor: 'white', cursor: 'pointer' }}
              >
                <option value="">All</option>
                {currencies.map((c) => (
                  <option key={c.code} value={c.code}>{c.code} — {c.name}</option>
                ))}
              </select>
            </div>
            <button
              onClick={handleRetentionSubmit}
              disabled={!isRetDatesValid() || retentionLoading}
              style={{
                padding: '10px 24px',
                backgroundColor: isRetDatesValid() && !retentionLoading ? '#4CAF50' : '#ccc',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: isRetDatesValid() && !retentionLoading ? 'pointer' : 'not-allowed',
                fontSize: '14px',
                fontWeight: '500',
                marginTop: '20px',
                transition: 'all 0.2s ease'
              }}
            >
              {retentionLoading ? 'Loading...' : 'Load Retention'}
            </button>
          </div>
        </div>

        {retentionLoading && <LoadingSpinner />}
        {retentionError && (
          <div style={{ padding: '12px', backgroundColor: '#fee', color: '#c33', borderRadius: '4px', marginBottom: '10px' }}>
            {retentionError}
          </div>
        )}
        {!retentionLoading && !retentionError && retentionChartData.length > 0 && (
          <>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '20px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              border: '1px solid #e0e0e0',
              marginBottom: '15px'
            }}>
              <BarChart<number>
                data={retentionChartData}
                formatXLabel={formatRetDayLabel}
                formatXLabelFull={formatRetDayLabelFull}
                formatYValue={(v) => `${v.toFixed(1)}%`}
                seriesLabels={{
                  d1Rate: 'D1 Retention',
                  d7Rate: 'D7 Retention',
                  d30Rate: 'D30 Retention',
                }}
                colors={['#FF9800', '#9C27B0', '#F44336']}
                height={350}
                minBarGroupWidth={70}
                showLegend={true}
              />
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                backgroundColor: 'white',
                borderRadius: '8px',
                overflow: 'hidden',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>
                <thead>
                  <tr style={{ backgroundColor: '#f5f5f5' }}>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Cohort Day</th>
                    <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>New Users (D0)</th>
                    <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>D1</th>
                    <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>D7</th>
                    <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>D30</th>
                  </tr>
                </thead>
                <tbody>
                  {retentionData.map((row) => (
                    <tr key={row.timestamp} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '12px', fontWeight: '500' }}>{formatRetDayLabelFull(row.timestamp)}</td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>{row.cohortSize}</td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>
                        {row.cohortSize > 0 ? `${row.d1Count} (${row.d1Rate.toFixed(1)}%)` : '—'}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>
                        {row.cohortSize > 0 ? `${row.d7Count} (${row.d7Rate.toFixed(1)}%)` : '—'}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>
                        {row.cohortSize > 0 ? `${row.d30Count} (${row.d30Rate.toFixed(1)}%)` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        {!retentionLoading && !retentionError && retentionData.length === 0 && retStartDate && retEndDate && (
          <div style={{ padding: '20px', textAlign: 'center', color: '#666', backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
            Select dates and click "Load Retention" to see cohort retention data
          </div>
        )}
      </div>

      {/* Top Users Sections */}
      <div style={{ marginTop: '40px' }}>
        <h2 style={{ marginBottom: '20px', color: '#333' }}>Top Users</h2>
        
        {/* Top by Volume */}
        <div style={{ marginBottom: '30px' }}>
          <div style={{ 
            padding: '15px', 
            backgroundColor: '#f9f9f9', 
            borderRadius: '8px',
            border: '1px solid #ddd',
            marginBottom: '15px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h3 style={{ margin: 0, color: '#333', fontSize: '16px' }}>Top Users by Volume (USD)</h3>
              <button
                onClick={() => updateTopConfig('volume', 'expanded', !topConfigs.volume.expanded)}
                style={{
                  padding: '4px 8px',
                  backgroundColor: 'transparent',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  color: '#666'
                }}
              >
                {topConfigs.volume.expanded ? '▼ Collapse' : '▶ Expand'}
              </button>
            </div>
            {topConfigs.volume.expanded && (
            <>
            <div style={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                <label style={{ fontSize: '12px', color: '#666' }}>Start Date</label>
                <input
                  type="date"
                  value={topConfigs.volume.startDate}
                  onChange={(e) => updateTopConfig('volume', 'startDate', e.target.value)}
                  max={getMaxDate()}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    minWidth: '150px'
                  }}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                <label style={{ fontSize: '12px', color: '#666' }}>End Date</label>
                <input
                  type="date"
                  value={topConfigs.volume.endDate}
                  onChange={(e) => updateTopConfig('volume', 'endDate', e.target.value)}
                  max={getMaxDate()}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    minWidth: '150px'
                  }}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                <label style={{ fontSize: '12px', color: '#666' }}>Timezone</label>
                <select
                  value={topConfigs.volume.timezoneOffset}
                  onChange={(e) => updateTopConfig('volume', 'timezoneOffset', Number(e.target.value))}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    minWidth: '150px',
                    backgroundColor: 'white',
                    cursor: 'pointer'
                  }}
                >
                  {timezoneOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <button
                onClick={() => loadTop('volume')}
                disabled={!isTopDatesValid('volume') || topConfigs.volume.loading}
                style={{
                  padding: '10px 24px',
                  backgroundColor: isTopDatesValid('volume') && !topConfigs.volume.loading ? '#4CAF50' : '#ccc',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: isTopDatesValid('volume') && !topConfigs.volume.loading ? 'pointer' : 'not-allowed',
                  fontSize: '14px',
                  fontWeight: '500',
                  marginTop: '20px',
                  transition: 'all 0.2s ease'
                }}
              >
                {topConfigs.volume.loading ? 'Loading...' : 'Load Top'}
              </button>
            </div>
            {topConfigs.volume.error && (
              <div style={{ marginTop: '10px', padding: '8px', backgroundColor: '#fee', color: '#c33', borderRadius: '4px', fontSize: '12px' }}>
                {topConfigs.volume.error}
              </div>
            )}
            </>
            )}
          </div>
          {topConfigs.volume.expanded && topConfigs.volume.data && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ 
                width: '100%', 
                borderCollapse: 'collapse',
                backgroundColor: 'white',
                borderRadius: '8px',
                overflow: 'hidden',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>
                <thead>
                  <tr style={{ backgroundColor: '#f5f5f5' }}>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Rank</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Username</th>
                    <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>Volume (USD)</th>
                  </tr>
                </thead>
                <tbody>
                  {topConfigs.volume.data.map((entry) => (
                    <tr key={entry.rank} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '12px', fontWeight: '500' }}>#{entry.rank}</td>
                      <td style={{ padding: '12px' }}>{entry.username}</td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>{formatTopValue('volume', entry.value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Top by GGR */}
        <div style={{ marginBottom: '30px' }}>
          <div style={{ 
            padding: '15px', 
            backgroundColor: '#f9f9f9', 
            borderRadius: '8px',
            border: '1px solid #ddd',
            marginBottom: '15px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h3 style={{ margin: 0, color: '#333', fontSize: '16px' }}>Top Users by GGR (USD)</h3>
              <button
                onClick={() => updateTopConfig('ggr', 'expanded', !topConfigs.ggr.expanded)}
                style={{
                  padding: '4px 8px',
                  backgroundColor: 'transparent',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  color: '#666'
                }}
              >
                {topConfigs.ggr.expanded ? '▼ Collapse' : '▶ Expand'}
              </button>
            </div>
            {topConfigs.ggr.expanded && (
            <>
            <div style={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                <label style={{ fontSize: '12px', color: '#666' }}>Start Date</label>
                <input
                  type="date"
                  value={topConfigs.ggr.startDate}
                  onChange={(e) => updateTopConfig('ggr', 'startDate', e.target.value)}
                  max={getMaxDate()}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    minWidth: '150px'
                  }}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                <label style={{ fontSize: '12px', color: '#666' }}>End Date</label>
                <input
                  type="date"
                  value={topConfigs.ggr.endDate}
                  onChange={(e) => updateTopConfig('ggr', 'endDate', e.target.value)}
                  max={getMaxDate()}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    minWidth: '150px'
                  }}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                <label style={{ fontSize: '12px', color: '#666' }}>Timezone</label>
                <select
                  value={topConfigs.ggr.timezoneOffset}
                  onChange={(e) => updateTopConfig('ggr', 'timezoneOffset', Number(e.target.value))}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    minWidth: '150px',
                    backgroundColor: 'white',
                    cursor: 'pointer'
                  }}
                >
                  {timezoneOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <button
                onClick={() => loadTop('ggr')}
                disabled={!isTopDatesValid('ggr') || topConfigs.ggr.loading}
                style={{
                  padding: '10px 24px',
                  backgroundColor: isTopDatesValid('ggr') && !topConfigs.ggr.loading ? '#4CAF50' : '#ccc',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: isTopDatesValid('ggr') && !topConfigs.ggr.loading ? 'pointer' : 'not-allowed',
                  fontSize: '14px',
                  fontWeight: '500',
                  marginTop: '20px',
                  transition: 'all 0.2s ease'
                }}
              >
                {topConfigs.ggr.loading ? 'Loading...' : 'Load Top'}
              </button>
            </div>
            {topConfigs.ggr.error && (
              <div style={{ marginTop: '10px', padding: '8px', backgroundColor: '#fee', color: '#c33', borderRadius: '4px', fontSize: '12px' }}>
                {topConfigs.ggr.error}
              </div>
            )}
            </>
            )}
          </div>
          {topConfigs.ggr.expanded && topConfigs.ggr.data && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ 
                width: '100%', 
                borderCollapse: 'collapse',
                backgroundColor: 'white',
                borderRadius: '8px',
                overflow: 'hidden',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>
                <thead>
                  <tr style={{ backgroundColor: '#f5f5f5' }}>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Rank</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Username</th>
                    <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>GGR (USD)</th>
                  </tr>
                </thead>
                <tbody>
                  {topConfigs.ggr.data.map((entry) => (
                    <tr key={entry.rank} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '12px', fontWeight: '500' }}>#{entry.rank}</td>
                      <td style={{ padding: '12px' }}>{entry.username}</td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>{formatTopValue('ggr', entry.value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Top by Win */}
        <div style={{ marginBottom: '30px' }}>
          <div style={{ 
            padding: '15px', 
            backgroundColor: '#f9f9f9', 
            borderRadius: '8px',
            border: '1px solid #ddd',
            marginBottom: '15px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h3 style={{ margin: 0, color: '#333', fontSize: '16px' }}>Top Users by Single Win (USD)</h3>
              <button
                onClick={() => updateTopConfig('win', 'expanded', !topConfigs.win.expanded)}
                style={{
                  padding: '4px 8px',
                  backgroundColor: 'transparent',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  color: '#666'
                }}
              >
                {topConfigs.win.expanded ? '▼ Collapse' : '▶ Expand'}
              </button>
            </div>
            {topConfigs.win.expanded && (
            <>
            <div style={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                <label style={{ fontSize: '12px', color: '#666' }}>Start Date</label>
                <input
                  type="date"
                  value={topConfigs.win.startDate}
                  onChange={(e) => updateTopConfig('win', 'startDate', e.target.value)}
                  max={getMaxDate()}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    minWidth: '150px'
                  }}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                <label style={{ fontSize: '12px', color: '#666' }}>End Date</label>
                <input
                  type="date"
                  value={topConfigs.win.endDate}
                  onChange={(e) => updateTopConfig('win', 'endDate', e.target.value)}
                  max={getMaxDate()}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    minWidth: '150px'
                  }}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                <label style={{ fontSize: '12px', color: '#666' }}>Timezone</label>
                <select
                  value={topConfigs.win.timezoneOffset}
                  onChange={(e) => updateTopConfig('win', 'timezoneOffset', Number(e.target.value))}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    minWidth: '150px',
                    backgroundColor: 'white',
                    cursor: 'pointer'
                  }}
                >
                  {timezoneOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <button
                onClick={() => loadTop('win')}
                disabled={!isTopDatesValid('win') || topConfigs.win.loading}
                style={{
                  padding: '10px 24px',
                  backgroundColor: isTopDatesValid('win') && !topConfigs.win.loading ? '#4CAF50' : '#ccc',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: isTopDatesValid('win') && !topConfigs.win.loading ? 'pointer' : 'not-allowed',
                  fontSize: '14px',
                  fontWeight: '500',
                  marginTop: '20px',
                  transition: 'all 0.2s ease'
                }}
              >
                {topConfigs.win.loading ? 'Loading...' : 'Load Top'}
              </button>
            </div>
            {topConfigs.win.error && (
              <div style={{ marginTop: '10px', padding: '8px', backgroundColor: '#fee', color: '#c33', borderRadius: '4px', fontSize: '12px' }}>
                {topConfigs.win.error}
              </div>
            )}
            </>
            )}
          </div>
          {topConfigs.win.expanded && topConfigs.win.data && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ 
                width: '100%', 
                borderCollapse: 'collapse',
                backgroundColor: 'white',
                borderRadius: '8px',
                overflow: 'hidden',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>
                <thead>
                  <tr style={{ backgroundColor: '#f5f5f5' }}>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Rank</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Username</th>
                    <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>Single Win (USD)</th>
                  </tr>
                </thead>
                <tbody>
                  {topConfigs.win.data.map((entry) => (
                    <tr key={entry.rank} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '12px', fontWeight: '500' }}>#{entry.rank}</td>
                      <td style={{ padding: '12px' }}>{entry.username}</td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>{formatTopValue('win', entry.value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Top by Spins */}
        <div style={{ marginBottom: '30px' }}>
          <div style={{ 
            padding: '15px', 
            backgroundColor: '#f9f9f9', 
            borderRadius: '8px',
            border: '1px solid #ddd',
            marginBottom: '15px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h3 style={{ margin: 0, color: '#333', fontSize: '16px' }}>Top Users by Number of Spins</h3>
              <button
                onClick={() => updateTopConfig('spins', 'expanded', !topConfigs.spins.expanded)}
                style={{
                  padding: '4px 8px',
                  backgroundColor: 'transparent',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  color: '#666'
                }}
              >
                {topConfigs.spins.expanded ? '▼ Collapse' : '▶ Expand'}
              </button>
            </div>
            {topConfigs.spins.expanded && (
            <>
            <div style={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                <label style={{ fontSize: '12px', color: '#666' }}>Start Date</label>
                <input
                  type="date"
                  value={topConfigs.spins.startDate}
                  onChange={(e) => updateTopConfig('spins', 'startDate', e.target.value)}
                  max={getMaxDate()}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    minWidth: '150px'
                  }}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                <label style={{ fontSize: '12px', color: '#666' }}>End Date</label>
                <input
                  type="date"
                  value={topConfigs.spins.endDate}
                  onChange={(e) => updateTopConfig('spins', 'endDate', e.target.value)}
                  max={getMaxDate()}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    minWidth: '150px'
                  }}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                <label style={{ fontSize: '12px', color: '#666' }}>Timezone</label>
                <select
                  value={topConfigs.spins.timezoneOffset}
                  onChange={(e) => updateTopConfig('spins', 'timezoneOffset', Number(e.target.value))}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    minWidth: '150px',
                    backgroundColor: 'white',
                    cursor: 'pointer'
                  }}
                >
                  {timezoneOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <button
                onClick={() => loadTop('spins')}
                disabled={!isTopDatesValid('spins') || topConfigs.spins.loading}
                style={{
                  padding: '10px 24px',
                  backgroundColor: isTopDatesValid('spins') && !topConfigs.spins.loading ? '#4CAF50' : '#ccc',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: isTopDatesValid('spins') && !topConfigs.spins.loading ? 'pointer' : 'not-allowed',
                  fontSize: '14px',
                  fontWeight: '500',
                  marginTop: '20px',
                  transition: 'all 0.2s ease'
                }}
              >
                {topConfigs.spins.loading ? 'Loading...' : 'Load Top'}
              </button>
            </div>
            {topConfigs.spins.error && (
              <div style={{ marginTop: '10px', padding: '8px', backgroundColor: '#fee', color: '#c33', borderRadius: '4px', fontSize: '12px' }}>
                {topConfigs.spins.error}
              </div>
            )}
            </>
            )}
          </div>
          {topConfigs.spins.expanded && topConfigs.spins.data && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ 
                width: '100%', 
                borderCollapse: 'collapse',
                backgroundColor: 'white',
                borderRadius: '8px',
                overflow: 'hidden',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>
                <thead>
                  <tr style={{ backgroundColor: '#f5f5f5' }}>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Rank</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Username</th>
                    <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>Spins</th>
                  </tr>
                </thead>
                <tbody>
                  {topConfigs.spins.data.map((entry) => (
                    <tr key={entry.rank} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '12px', fontWeight: '500' }}>#{entry.rank}</td>
                      <td style={{ padding: '12px' }}>{entry.username}</td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>{formatTopValue('spins', entry.value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GameUserStatsPage;
